import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  CalendarCheck,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Heart,
  Shirt,
} from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useLang } from "@/context/LanguageContext";
import { getProduct } from "@/services/catalog";
import { formatPrice, formatDate, todayISO } from "@/lib/utils";
import { products, DELIVERY_WINDOW, SHOPIFY_VARIANTS, SHOPIFY_ADDONS } from "@/data/products";
import { track } from "@/utils/metaPixel";
import { extraAddonId } from "@/utils/shopifyCheckout";
import { ReviewsCarousel } from "@/components/ReviewsCarousel";

const AccordionItem = ({ title, open, onToggle, children, testId }) => (
    <div className="border-b border-stone-200">
        <button
            onClick={onToggle}
            data-testid={testId}
            className="w-full flex items-center justify-between gap-4 py-4 text-left"
        >
            <span className="text-sm sm:text-base font-semibold text-stone-900">{title}</span>
            <ChevronDown
                className={`h-4 w-4 text-bordeaux shrink-0 transition-transform duration-300 ${
                    open ? "rotate-180" : ""
                }`}
                strokeWidth={2}
            />
        </button>
        <div
            className={`grid transition-all duration-300 ease-out ${
                open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
            }`}
        >
            <div className="overflow-hidden">
                <div className="pb-4 text-sm text-stone-600 leading-relaxed">{children}</div>
            </div>
        </div>
    </div>
);

export default function ProductPage() {
    const { handle } = useParams();
    const { lang, t } = useLang();
    const { addItem, openCart } = useCart();
    const product = getProduct(handle);

    const [activeImage, setActiveImage] = useState(0);
    const [roseCount, setRoseCount] = useState(
        product?.options.roseCounts ? product.options.roseCounts[0].count : null
    );
    const [shirtText, setShirtText] = useState("");
    const [message, setMessage] = useState("");
    const [deliveryDate, setDeliveryDate] = useState("");
    const [openAcc, setOpenAcc] = useState(-1);
    const [teddyColor, setTeddyColor] = useState(
        product?.teddyOptions ? product.teddyOptions.colors[0].id : null
    );
    const [hasShirt, setHasShirt] = useState(false);
    const [shirtColor, setShirtColor] = useState(
        product?.teddyOptions ? product.teddyOptions.shirt.colors[0].id : null
    );
    const [hasShirtText, setHasShirtText] = useState(false);
    const [packExtra, setPackExtra] = useState(false);
    const [shirtName, setShirtName] = useState("");
    const [shirtError, setShirtError] = useState(false);
    const [packExtraText, setPackExtraText] = useState("");
    const [deliveryAddress, setDeliveryAddress] = useState("");
    const [postalCode, setPostalCode] = useState("");
    const [timeSlot, setTimeSlot] = useState("asap");
    const [recipientName, setRecipientName] = useState("");
    const [recipientPhone, setRecipientPhone] = useState("+34 ");
    const [phoneTouched, setPhoneTouched] = useState(false);
    /* Téléphone : chiffres, espaces et « + » uniquement ; 6 à 15 chiffres
       (indicatif +34 prérempli, numéros étrangers acceptés). */
    const phoneOk = (v) => {
        const s = String(v).trim();
        if (!s) return true;
        const digits = s.replace(/[\s+]/g, "");
        return /^\+?[0-9\s]{6,20}$/.test(s) && digits.length >= 6 && digits.length <= 15;
    };
    const [recipientPlace, setRecipientPlace] = useState("");
    const [additionalInfo, setAdditionalInfo] = useState("");
    const [selectedExtras, setSelectedExtras] = useState([]);
    const [redBalloon, setRedBalloon] = useState(false);
    const [balloonTextOn, setBalloonTextOn] = useState(false);
    const [balloonTextValue, setBalloonTextValue] = useState("");
    const [now, setNow] = useState(() => new Date());

    useEffect(() => {
        if (product?.id !== "romantic-pack-supreme" && product?.id !== "fresh-roses") return;
        const id = setInterval(() => setNow(new Date()), 15000);
        return () => clearInterval(id);
    }, [product?.id]);

    useEffect(() => {
        setActiveImage(0);
        setRoseCount(product?.options.roseCounts ? product.options.roseCounts[0].count : null);
        setShirtText("");
        setMessage("");
        setDeliveryDate("");
        setOpenAcc(-1);
        setTeddyColor(product?.teddyOptions ? product.teddyOptions.colors[0].id : null);
        setHasShirt(false);
        setShirtColor(product?.teddyOptions ? product.teddyOptions.shirt.colors[0].id : null);
        setHasShirtText(false);
        setPackExtra(false);
        setShirtName("");
        setShirtError(false);
        setPackExtraText("");
        setDeliveryAddress("");
        setPostalCode("");
        setTimeSlot("asap");
        setRecipientName("");
        setRecipientPhone("+34 ");
        setRecipientPlace("");
        setAdditionalInfo("");
        setSelectedExtras([]);
        setRedBalloon(false);
        setBalloonTextOn(false);
        setBalloonTextValue("");
    }, [handle]);

    /* ViewContent : hook AVANT tout retour anticipé (règle des hooks React).
       Le corps lit les valeurs calculées plus bas (exécutées après le rendu). */
    useEffect(() => {
        if (!product) return;
        track("ViewContent", {
            content_name: product.name[lang],
            content_ids: [String(currentVariantId || product.id)],
            content_type: "product",
            value: Number(price.toFixed(2)),
            currency: "EUR",
        });
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, [product?.id, roseCount, teddyColor, hasShirt, shirtColor, hasShirtText, selectedExtras.join(","), packExtra, redBalloon, balloonTextOn, lang]);

    if (!product) {
        return (
            <main className="pt-24 pb-16 flex flex-col items-center gap-5 px-4 text-center">
                <Heart className="h-8 w-8 text-bordeaux/30" strokeWidth={1.5} />
                <p className="font-bold text-xl text-ink">{t("cart.empty")}</p>
                <Link
                    to="/"
                    data-testid="product-not-found-back"
                    className="px-8 py-3 bg-bordeaux text-white text-sm font-semibold rounded-lg"
                >
                    {t("product.back")}
                </Link>
            </main>
        );
    }

    const selectedVariant = product.options.roseCounts?.find((v) => v.count === roseCount);
    const isTeddy = product.id === "custom-teddy";
    const isPack = product.id === "romantic-pack-supreme";
    const isCaja = product.id === "surprise-box-balloons";
    const isRamo = product.id === "fresh-roses";
    const teddy = product.teddyOptions;
    const teddyImageIndex = (colorId, withShirt) => {
        const color = teddy.colors.find((c) => c.id === colorId);
        return withShirt ? (color?.image ?? 0) + 1 : (color?.image ?? 0);
    };
    const shirtExtra = isTeddy && hasShirt ? teddy.shirt.price : 0;
    const textExtra = isTeddy && hasShirt && hasShirtText ? teddy.shirtText.price : 0;
    const packExtraPrice = isPack && packExtra ? product.packExtras.personalize.price : 0;
    const extrasTotal = (product.extras || [])
        .filter((e) => selectedExtras.includes(e.id))
        .reduce((s, e) => s + e.price, 0);
    const ramoExtrasPrice = isRamo
        ? (redBalloon ? product.roseExtras.redBalloon.price : 0) +
          (balloonTextOn ? product.roseExtras.balloonText.price : 0)
        : 0;
    const hasFreeDelivery =
        1 +
            selectedExtras.length +
            (isRamo && redBalloon ? 1 : 0) +
            (isRamo && balloonTextOn ? 1 : 0) +
            (isPack && packExtra ? 1 : 0) +
            (isTeddy && hasShirtText ? 1 : 0) >=
        2;
    const toggleExtra = (id) => {
        const wasIn = selectedExtras.includes(id);
        setSelectedExtras((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
        /* AddToCart quand un extra est AJOUTÉ (pas retiré). */
        if (!wasIn) {
            const x = (product.extras || []).find((e) => e.id === id);
            const addonId = x ? extraAddonId(x.name[lang]) : undefined;
            if (x)
                track("AddToCart", {
                    content_name: x.name[lang],
                    content_ids: [String(addonId || x.id)],
                    content_type: "product",
                    value: Number(x.price),
                    currency: "EUR",
                });
        }
    };
    const price =
        (selectedVariant ? selectedVariant.price : product.basePrice) +
        shirtExtra +
        textExtra +
        packExtraPrice +
        extrasTotal +
        ramoExtrasPrice;

    /* --- Meta Pixel : ID de variante Shopify de la sélection courante --- */
    const teddyShirtKey =
        isTeddy && hasShirt && shirtColor ? String(shirtColor).toLowerCase() : "none";
    const currentVariantId = isRamo || isPack
        ? SHOPIFY_VARIANTS[product.id]?.[String(roseCount)]
        : isCaja
        ? SHOPIFY_VARIANTS["surprise-box-balloons"].default
        : isTeddy
        ? SHOPIFY_VARIANTS["custom-teddy"][`${teddyColor}|${teddyShirtKey}`]
        : undefined;

    let packDelivery = null;
    if (isPack || isRamo) {
        const cutoffHour = isPack
            ? product.packExtras.cutoffHour
            : product.roseExtras.cutoffHour;
        const cutoff = new Date(now);
        cutoff.setHours(cutoffHour, 0, 0, 0);
        if (now < cutoff) {
            const mins = Math.max(0, Math.floor((cutoff - now) / 60000));
            packDelivery = {
                today: true,
                time: `${Math.floor(mins / 60)} h ${mins % 60} min`,
            };
        } else {
            packDelivery = { today: false };
        }
    }

    const slotLabel = timeSlot === "asap" ? t("pack.asapOption") : timeSlot;
    const slotOptions = (() => {
        const { startHour, endHour, stepMinutes } = DELIVERY_WINDOW;
        const arr = [];
        for (let m = startHour * 60; m <= endHour * 60; m += stepMinutes) {
            arr.push(
                `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`
            );
        }
        return arr;
    })();
    const ExtrasSection = () => (
        <div className="mt-7" data-testid="extras-section">
            <p className="text-sm font-bold text-ink">
                {!isPack && !isTeddy && "1. "}
                {t("extras.title")}
            </p>
            <p className="mt-1 text-xs text-stone-500">{t("extras.subtitle")}</p>
            <p className="mt-1.5 text-xs font-semibold text-bordeaux" data-testid="extras-free-hint">
                {t("extras.freeHint")}
            </p>
            <div className="mt-3 space-y-2.5 max-w-md">
                {product.extras.map((x) => {
                    const on = selectedExtras.includes(x.id);
                    return (
                        <button
                            key={x.id}
                            onClick={() => toggleExtra(x.id)}
                            data-testid={`extra-${x.id}`}
                            className={`w-full flex items-center gap-3 border rounded-xl p-2.5 text-left transition-colors touch-manipulation select-none active:scale-[0.99] ${
                                on
                                    ? "border-bordeaux bg-bordeaux/5"
                                    : "border-stone-200 bg-white hover:border-stone-400"
                            }`}
                        >
                            <img
                                src={x.image}
                                alt={x.name[lang]}
                                className="h-16 w-16 rounded-lg object-cover shrink-0"
                                loading="lazy"
                            />
                            <span className="min-w-0 flex-1">
                                <span className="block text-sm font-bold text-stone-900 leading-tight">
                                    {x.name[lang]}
                                </span>
                                <span className="block text-xs text-stone-500 mt-0.5">
                                    {x.desc[lang]}
                                </span>
                                <span className="block text-sm font-bold text-bordeaux mt-1">
                                    + {formatPrice(x.price, lang)}
                                </span>
                            </span>
                            <span
                                className={`shrink-0 px-3 py-2 rounded-lg text-xs font-bold border transition-colors ${
                                    on
                                        ? "bg-bordeaux border-bordeaux text-white"
                                        : "border-stone-300 text-stone-800"
                                }`}
                            >
                                {on ? `✓ ${t("extras.added")}` : t("extras.add")}
                            </span>
                        </button>
                    );
                })}
            </div>
            {hasFreeDelivery && (
                <div
                    className="mt-3 inline-flex items-center gap-1.5 bg-bordeaux/5 border border-bordeaux/20 rounded-lg px-3 py-2"
                    data-testid="free-delivery-badge"
                >
                    <Check
                        className="h-4 w-4 text-bordeaux shrink-0"
                        strokeWidth={2.5}
                    />
                    <span className="text-xs font-bold text-bordeaux">
                        {t("extras.freeDelivery")}
                    </span>
                </div>
            )}
        </div>
    );

    const DeliveryInfoSection = ({ num }) => (
        <div
            className="mt-7"
            data-testid={isPack ? "pack-delivery-info-section" : "delivery-info-section"}
        >
            <p className="text-sm font-bold text-ink mb-3">
                {num ? `${num}. ` : ""}
                {t("pack.zoneTitle")}
            </p>
            <div className="max-w-md">
                <div className="grid grid-cols-2 gap-2.5">
                    <input
                        type="text"
                        value={recipientName}
                        maxLength={60}
                        onChange={(e) => setRecipientName(e.target.value)}
                        placeholder={t("pack.recipientName")}
                        data-testid="pack-recipient-name"
                        className="w-full bg-white border border-stone-300 focus:border-bordeaux rounded-lg outline-none px-4 py-3 text-ink placeholder:text-stone-400 transition-colors"
                    />
                    <input
                        type="tel"
                        inputMode="tel"
                        value={recipientPhone}
                        maxLength={20}
                        onChange={(e) => {
                            setRecipientPhone(e.target.value.replace(/[^0-9\s+]/g, ""));
                            setPhoneTouched(true);
                        }}
                        onBlur={() => setPhoneTouched(true)}
                        placeholder={t("pack.recipientPhone")}
                        data-testid="pack-recipient-phone"
                        className={`w-full bg-white border rounded-lg outline-none px-4 py-3 text-ink placeholder:text-stone-400 transition-colors ${
                            phoneTouched && !phoneOk(recipientPhone)
                                ? "border-red-500"
                                : "border-stone-300 focus:border-bordeaux"
                        }`}
                    />
                    {phoneTouched && !phoneOk(recipientPhone) && (
                        <p className="mt-1 text-xs text-red-600" data-testid="phone-error">
                            {lang === "es"
                                ? "Introduce un número de teléfono válido"
                                : "Enter a valid phone number"}
                        </p>
                    )}
                </div>
                <input
                    type="text"
                    value={deliveryAddress}
                    maxLength={120}
                    onChange={(e) => setDeliveryAddress(e.target.value)}
                    placeholder={t("pack.addressPlaceholder")}
                    data-testid="pack-address-input"
                    className="mt-2.5 w-full bg-white border border-stone-300 focus:border-bordeaux rounded-lg outline-none px-4 py-3 text-ink placeholder:text-stone-400 transition-colors"
                />
                <div className="grid grid-cols-2 gap-2.5 mt-2.5">
                    <input
                        type="text"
                        inputMode="numeric"
                        value={postalCode}
                        maxLength={6}
                        onChange={(e) => setPostalCode(e.target.value)}
                        placeholder={t("pack.postalPlaceholder")}
                        data-testid="pack-postal-input"
                        className="w-full bg-white border border-stone-300 focus:border-bordeaux rounded-lg outline-none px-4 py-3 text-ink placeholder:text-stone-400 transition-colors"
                    />
                    <input
                        type="text"
                        value={recipientPlace}
                        maxLength={80}
                        onChange={(e) => setRecipientPlace(e.target.value)}
                        placeholder={t("pack.recipientPlace")}
                        data-testid="pack-recipient-place"
                        className="w-full bg-white border border-stone-300 focus:border-bordeaux rounded-lg outline-none px-4 py-3 text-ink placeholder:text-stone-400 transition-colors"
                    />
                </div>
                <textarea
                    value={additionalInfo}
                    maxLength={200}
                    rows={2}
                    onChange={(e) => setAdditionalInfo(e.target.value)}
                    placeholder={t("pack.additionalPlaceholder")}
                    data-testid="pack-additional-info"
                    className="mt-2.5 w-full bg-white border border-stone-300 focus:border-bordeaux rounded-lg outline-none px-4 py-3 text-ink placeholder:text-stone-400 transition-colors resize-none"
                />
            </div>
        </div>
    );

    const WhenSection = ({ num }) => (
        <div
            className="mt-7"
            data-testid={isPack ? "pack-when-section" : "delivery-when-section"}
        >
            <p className="text-sm font-bold text-ink mb-3">
                {num ? `${num}. ` : ""}
                {t("pack.whenTitle")}
            </p>
            <div className="max-w-md">
                <p className="text-xs font-semibold text-stone-600 mb-1.5">{t("product.opt4")}</p>
                <input
                    type="date"
                    value={deliveryDate}
                    min={todayISO()}
                    onChange={(e) => setDeliveryDate(e.target.value)}
                    data-testid="delivery-date-select"
                    className="w-full bg-white border border-stone-300 focus:border-bordeaux rounded-lg outline-none px-4 py-3 text-ink transition-colors"
                />
                {deliveryDate && (
                    <p className="mt-1.5 text-[11px] text-stone-500">
                        {t("cart.deliveryOn")}: {formatDate(deliveryDate, lang)}
                    </p>
                )}
                <p className="mt-4 text-xs font-semibold text-stone-600 mb-1.5">
                    {t("pack.slotLabel")}
                </p>
                <div className="relative">
                    <select
                        value={timeSlot}
                        onChange={(e) => setTimeSlot(e.target.value)}
                        data-testid="pack-slot-select"
                        className="w-full bg-white border border-stone-300 focus:border-bordeaux rounded-lg outline-none px-4 py-3 text-ink transition-colors appearance-none pr-10"
                    >
                        <option value="asap">{t("pack.asapOption")}</option>
                        {slotOptions.map((s) => (
                            <option
                                key={s}
                                value={s}
                                disabled={DELIVERY_WINDOW.disabledSlots.includes(s)}
                            >
                                {s}
                            </option>
                        ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400 pointer-events-none" />
                </div>
            </div>
        </div>
    );

    const handleAdd = () => {
        if (isPack && !shirtName.trim()) {
            setShirtError(true);
            document
                .getElementById("pack-shirt-name")
                ?.scrollIntoView({ behavior: "smooth", block: "center" });
            return;
        }
        if (!phoneOk(recipientPhone)) {
            setPhoneTouched(true);
            document
                .querySelector('[data-testid="pack-recipient-phone"]')
                ?.scrollIntoView({ behavior: "smooth", block: "center" });
            return;
        }
        setShirtError(false);
        track("AddToCart", {
            content_name: product.name[lang],
            content_ids: [String(currentVariantId || product.id)],
            content_type: "product",
            value: Number(price.toFixed(2)),
            currency: "EUR",
        });
        addItem({
            productId: product.id,
            handle: product.handle,
            name: product.name[lang],
            image: product.images[0],
            unitPrice: price,
            qty: 1,
            options: isPack
                ? {
                      roseCount: selectedVariant ? roseCount : undefined,
                      packExtra: packExtra,
                      message: message.trim() || undefined,
                      deliveryDate: deliveryDate || undefined,
                      properties: [
                          ["Ramo", `${roseCount} ${t("product.rosesUnit")}`],
                          ["Nombre en la camiseta", shirtName.trim()],
                          ...(packExtra
                              ? [["Texto globo/embalaje", packExtraText.trim() || "—"]]
                              : []),
                          ...(product.extras
                              ? product.extras
                                    .filter((e) => selectedExtras.includes(e.id))
                                    .map((e) => ["Extra", `${e.name[lang]} (+${e.price} €)`])
                              : []),
                          ...(message.trim()
                              ? [["Mensaje tarjeta", message.trim()]]
                              : []),
                          ...(deliveryAddress.trim()
                              ? [["Dirección", deliveryAddress.trim()]]
                              : []),
                          ...(postalCode.trim()
                              ? [["Código postal", postalCode.trim()]]
                              : []),
                          ...(deliveryDate
                              ? [["Fecha de entrega", formatDate(deliveryDate, lang)]]
                              : []),
                          ["Franja horaria", slotLabel],
                          ...(recipientName.trim()
                              ? [["Destinatario", recipientName.trim()]]
                              : []),
                          ...(recipientPhone.trim() ? [["Teléfono", recipientPhone.trim()]] : []),
                          ...(recipientPlace.trim() ? [["Lugar", recipientPlace.trim()]] : []),
                          ...(additionalInfo.trim()
                              ? [["Información adicional", additionalInfo.trim()]]
                              : []),
                      ],
                  }
                : {
                      roseCount: selectedVariant ? roseCount : undefined,
                      teddyColor: isTeddy ? teddyColor : undefined,
                      teddyColorLabel: isTeddy
                          ? teddy.colors.find((c) => c.id === teddyColor)?.label[lang]
                          : undefined,
                      shirt: isTeddy ? hasShirt : undefined,
                      shirtColor: isTeddy && hasShirt ? shirtColor : undefined,
                      shirtColorLabel:
                          isTeddy && hasShirt
                              ? teddy.shirt.colors.find((c) => c.id === shirtColor)?.label[lang]
                              : undefined,
                      shirtText: isTeddy && hasShirt && hasShirtText ? shirtText.trim() : undefined,
                      message:
                          isCaja && !selectedExtras.includes("tarjeta-premium")
                              ? undefined
                              : message.trim() || undefined,
                      deliveryDate: deliveryDate || undefined,
                      properties: [
                          ...(deliveryAddress.trim()
                              ? [["Dirección", deliveryAddress.trim()]]
                              : []),
                          ...(postalCode.trim()
                              ? [["Código postal", postalCode.trim()]]
                              : []),
                          ["Franja horaria", slotLabel],
                          ...(recipientName.trim()
                              ? [["Destinatario", recipientName.trim()]]
                              : []),
                          ...(recipientPhone.trim() ? [["Teléfono", recipientPhone.trim()]] : []),
                          ...(recipientPlace.trim() ? [["Lugar", recipientPlace.trim()]] : []),
                          ...(additionalInfo.trim()
                              ? [["Información adicional", additionalInfo.trim()]]
                              : []),
                          ...(product.extras
                              ? product.extras
                                    .filter((e) => selectedExtras.includes(e.id))
                                    .map((e) => ["Extra", `${e.name[lang]} (+${e.price} €)`])
                              : []),
                          ...(isRamo && redBalloon
                              ? [
                                    [
                                        "Globo de corazón con helio",
                                        `+${product.roseExtras.redBalloon.price} €`,
                                    ],
                                ]
                              : []),
                          ...(isRamo && balloonTextOn
                              ? [
                                    [
                                        "Escritura en el globo",
                                        balloonTextValue.trim() ||
                                            `+${product.roseExtras.balloonText.price} €`,
                                    ],
                                ]
                              : []),
                      ],
                  },
        });
        toast(t("product.added"), {
            description: `${product.name[lang]} · ${formatPrice(price, lang)}`,
        });
        openCart();
    };

    return (
        <main className="bg-white min-h-screen pb-28 lg:pb-16">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
                <p className="text-xs text-stone-400" data-testid="product-breadcrumb">
                    <Link to="/" className="hover:text-bordeaux transition-colors">
                        {t("product.back")}
                    </Link>
                    <span className="mx-1.5">/</span>
                    <span className="text-stone-600">{product.name[lang]}</span>
                </p>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 lg:py-8">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14">
                    <div className="lg:sticky lg:top-36 self-start">
                        <div className="relative rounded-2xl overflow-hidden border border-stone-200 bg-stone-100">
                            <img
                                src={product.images[activeImage] || product.images[0]}
                                alt={product.name[lang]}
                                data-testid="product-gallery-main"
                                className="w-full aspect-square object-cover"
                            />
                            {product.images.length > 1 && (
                                <>
                                    <button
                                        onClick={() =>
                                            setActiveImage(
                                                (activeImage - 1 + product.images.length) % product.images.length
                                            )
                                        }
                                        data-testid="gallery-prev"
                                        aria-label="Anterior"
                                        className="absolute left-3 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-white/90 shadow flex items-center justify-center text-stone-700 hover:text-bordeaux transition-colors"
                                    >
                                        <ChevronLeft className="h-5 w-5" strokeWidth={2} />
                                    </button>
                                    <button
                                        onClick={() =>
                                            setActiveImage((activeImage + 1) % product.images.length)
                                        }
                                        data-testid="gallery-next"
                                        aria-label="Siguiente"
                                        className="absolute right-3 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-white/90 shadow flex items-center justify-center text-stone-700 hover:text-bordeaux transition-colors"
                                    >
                                        <ChevronRight className="h-5 w-5" strokeWidth={2} />
                                    </button>
                                </>
                            )}
                        </div>
                        {product.images.length > 1 && (
                            <div className="mt-3 flex gap-2.5 justify-start">
                                {product.images.map((src, i) => (
                                    <button
                                        key={i}
                                        onClick={() => setActiveImage(i)}
                                        data-testid={`product-gallery-thumb-${i}`}
                                        className={`w-14 h-14 sm:w-16 sm:h-16 rounded-lg overflow-hidden border-2 transition-colors ${
                                            activeImage === i ? "border-bordeaux" : "border-stone-200"
                                        }`}
                                        aria-label={`Imagen ${i + 1}`}
                                    >
                                        <img src={src} alt="" className="h-full w-full object-cover" />
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    <div>
                        {product.badge && (
                            <span className="inline-block bg-bordeaux text-white text-[10px] font-semibold uppercase tracking-wide px-2.5 py-1 rounded-md">
                                {product.badge[lang]}
                            </span>
                        )}
                        <h1 className="mt-3 font-serif text-3xl sm:text-4xl lg:text-5xl text-stone-900 leading-tight">
                            {product.name[lang]}
                        </h1>
                        <p className="mt-2 font-serif italic text-2xl sm:text-3xl text-bordeaux" data-testid="product-price">
                            {t("products.from")} {formatPrice(price, lang)}
                        </p>
                        <p className="mt-4 text-sm sm:text-base text-stone-600 leading-relaxed">
                            {product.desc[lang]}
                        </p>

                        {isPack && (
                            <div className="mt-5 max-w-md" data-testid="pack-includes">
                                <p className="text-sm font-bold text-ink">{t("pack.includesTitle")}</p>
                                <ul className="mt-2.5 space-y-1.5">
                                    {[
                                        { emoji: "🌹", key: "includes1" },
                                        { emoji: "🧸", key: "includes2" },
                                        { emoji: "🎁", key: "includes3" },
                                        { emoji: "💌", key: "includes4" },
                                    ].map(({ emoji, key }) => (
                                        <li
                                            key={key}
                                            className="flex items-start gap-2 text-sm text-stone-700"
                                        >
                                            <span
                                                className="leading-none shrink-0 mt-0.5"
                                                aria-hidden="true"
                                            >
                                                {emoji}
                                            </span>
                                            {t(`pack.${key}`)}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {product.options.roseCounts && (
                            <div className="mt-7">
                                <p className="text-sm font-bold text-ink mb-3">
                                    1. {t("product.opt1")}
                                    {isPack && (
                                        <span className="ml-2 text-xs font-normal text-stone-500">
                                            {t("pack.rosesHint")}
                                        </span>
                                    )}
                                </p>
                                <div
                                    className={`grid gap-2.5 max-w-md ${
                                        product.options.roseCounts.length > 3
                                            ? "grid-cols-2 sm:grid-cols-4"
                                            : "grid-cols-3"
                                    }`}
                                >
                                    {product.options.roseCounts.map((v) => (
                                        <button
                                            key={v.count}
                                            onClick={() => {
                                                setRoseCount(v.count);
                                                const idx = product.options.roseCounts.findIndex(
                                                    (x) => x.count === v.count
                                                );
                                                if (idx > -1 && idx < product.images.length) {
                                                    setActiveImage(idx);
                                                }
                                            }}
                                            data-testid={`rose-count-option-${v.count}`}
                                            className={`py-3 px-2 border rounded-lg text-center transition-colors touch-manipulation select-none active:scale-[0.97] ${
                                                roseCount === v.count
                                                    ? "bg-bordeaux border-bordeaux text-white"
                                                    : "border-stone-300 text-stone-800 hover:border-bordeaux hover:text-bordeaux"
                                            }`}
                                        >
                                            <span className="block text-sm font-bold">
                                                {v.count} {t("product.rosesUnit")}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {isPack && (
                            <>
                                <div className="mt-7" data-testid="pack-delivery-box">
                                    <div className="border border-stone-200 rounded-xl px-4 py-3.5 bg-white">
                                        <p className="flex items-start gap-2.5 text-sm">
                                            <Check
                                                className="h-4 w-4 text-bordeaux mt-0.5 shrink-0"
                                                strokeWidth={3}
                                            />
                                            <span>
                                                {packDelivery?.today ? (
                                                    <>
                                                        <span className="font-bold text-stone-900">
                                                            {t("pack.deliveryToday")}{" "}
                                                            <span className="text-bordeaux">
                                                                {t("pack.deliveryIn")} {packDelivery.time}
                                                            </span>
                                                        </span>
                                                        <span className="block text-xs text-stone-500 mt-0.5">
                                                            {t("pack.deliverySub")}
                                                        </span>
                                                    </>
                                                ) : (
                                                    <>
                                                        <span className="font-bold text-stone-900">
                                                            {t("pack.deliveryTomorrow")}
                                                        </span>
                                                        <span className="block text-xs text-stone-500 mt-0.5">
                                                            {t("pack.deliveryTomorrowSub")}
                                                        </span>
                                                    </>
                                                )}
                                            </span>
                                        </p>
                                    </div>
                                </div>

                                <div className="mt-7" id="pack-step-shirt">
                                    <p className="text-sm font-bold text-ink mb-3">
                                        2. {t("pack.shirtNameTitle")}
                                    </p>
                                    <div className="max-w-md">
                                        <input
                                            id="pack-shirt-name"
                                            type="text"
                                            value={shirtName}
                                            maxLength={product.packExtras.shirtNameMaxLength}
                                            onChange={(e) => {
                                                setShirtName(e.target.value);
                                                if (shirtError) setShirtError(false);
                                            }}
                                            placeholder={t("pack.shirtNamePlaceholder")}
                                            data-testid="pack-shirt-name-input"
                                            className={`w-full bg-white border rounded-lg outline-none px-4 py-3 text-ink placeholder:text-stone-400 transition-colors ${
                                                shirtError
                                                    ? "border-red-500"
                                                    : "border-stone-300 focus:border-bordeaux"
                                            }`}
                                        />
                                        <p
                                            className={`mt-1.5 text-xs ${
                                                shirtError
                                                    ? "text-red-600 font-semibold"
                                                    : "text-stone-500"
                                            }`}
                                            data-testid={shirtError ? "pack-shirt-error" : undefined}
                                        >
                                            {shirtError
                                                ? t("pack.shirtNameError")
                                                : t("pack.shirtNameHint")}
                                        </p>
                                    </div>
                                </div>

                                <div className="mt-7">
                                    <p className="text-sm font-bold text-ink mb-3">
                                        3. {t("pack.surpriseTitle")}
                                    </p>
                                    <div className="max-w-md">
                                        <button
                                            onClick={() => setPackExtra(!packExtra)}
                                            data-testid="pack-personalize-checkbox"
                                            className="w-full flex items-start justify-between gap-3 border border-stone-200 rounded-lg px-4 py-3.5 text-left hover:border-stone-400 transition-colors bg-white touch-manipulation select-none"
                                        >
                                            <span className="flex items-start gap-3">
                                                <span
                                                    className={`mt-0.5 h-5 w-5 rounded flex items-center justify-center border shrink-0 transition-colors ${
                                                        packExtra
                                                            ? "bg-bordeaux border-bordeaux"
                                                            : "bg-white border-stone-300"
                                                    }`}
                                                >
                                                    {packExtra && (
                                                        <Check
                                                            className="h-3.5 w-3.5 text-white"
                                                            strokeWidth={3}
                                                        />
                                                    )}
                                                </span>
                                                <span className="text-sm font-bold text-stone-900">
                                                    {t("pack.surpriseOption")}
                                                    <span className="block text-xs font-normal text-stone-500 mt-0.5">
                                                        {t("product.optional")}
                                                    </span>
                                                </span>
                                            </span>
                                            <span className="text-sm font-bold text-bordeaux shrink-0">
                                                + {formatPrice(product.packExtras.personalize.price, lang)}
                                            </span>
                                        </button>
                                        {packExtra && (
                                            <div className="mt-3">
                                                <textarea
                                                    value={packExtraText}
                                                    maxLength={product.packExtras.personalize.maxLength}
                                                    rows={2}
                                                    onChange={(e) => setPackExtraText(e.target.value)}
                                                    placeholder={t("pack.surprisePlaceholder")}
                                                    data-testid="pack-extra-text-input"
                                                    className="w-full bg-white border border-stone-300 focus:border-bordeaux rounded-lg outline-none px-4 py-3 text-ink placeholder:text-stone-400 transition-colors resize-none"
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {product.extras && ExtrasSection()}

                                <div className="mt-7" data-testid="pack-card-section">
                                    <p className="text-sm font-bold text-ink mb-3">
                                        {t("product.opt3")}
                                    </p>
                                    <div className="relative max-w-md">
                                        <textarea
                                            value={message}
                                            maxLength={200}
                                            rows={3}
                                            onChange={(e) => setMessage(e.target.value)}
                                            placeholder={t("product.messagePlaceholder")}
                                            data-testid="card-message-input"
                                            className="w-full bg-white border border-stone-300 focus:border-bordeaux rounded-lg outline-none px-4 py-3 text-ink placeholder:text-stone-400 transition-colors resize-none"
                                        />
                                        <span className="absolute right-3 bottom-3 text-[11px] text-stone-400">
                                            {message.length}/{200}
                                        </span>
                                    </div>
                                </div>

                                {DeliveryInfoSection({ num: 4 })}

                                {WhenSection({ num: 5 })}
                            </>
                        )}

                        {isRamo && (
                            <>
                                <div className="mt-7" data-testid="ramo-delivery-box">
                                    <div className="border border-stone-200 rounded-xl px-4 py-3.5 bg-white">
                                        <p className="flex items-start gap-2.5 text-sm">
                                            <Check
                                                className="h-4 w-4 text-bordeaux mt-0.5 shrink-0"
                                                strokeWidth={3}
                                            />
                                            <span>
                                                {packDelivery?.today ? (
                                                    <>
                                                        <span className="font-bold text-stone-900">
                                                            {t("pack.deliveryToday")}{" "}
                                                            <span className="text-bordeaux">
                                                                {t("pack.deliveryIn")} {packDelivery.time}
                                                            </span>
                                                        </span>
                                                        <span className="block text-xs text-stone-500 mt-0.5">
                                                            {t("pack.deliverySub")}
                                                        </span>
                                                    </>
                                                ) : (
                                                    <>
                                                        <span className="font-bold text-stone-900">
                                                            {t("pack.deliveryTomorrow")}
                                                        </span>
                                                        <span className="block text-xs text-stone-500 mt-0.5">
                                                            {t("pack.deliveryTomorrowSub")}
                                                        </span>
                                                    </>
                                                )}
                                            </span>
                                        </p>
                                    </div>
                                </div>

                                <div className="mt-7" data-testid="ramo-card-section">
                                    <p className="text-sm font-bold text-ink mb-3">
                                        {t("product.opt3")}
                                    </p>
                                    <div className="relative max-w-md">
                                        <textarea
                                            value={message}
                                            maxLength={200}
                                            rows={3}
                                            onChange={(e) => setMessage(e.target.value)}
                                            placeholder={t("product.messagePlaceholder")}
                                            data-testid="card-message-input"
                                            className="w-full bg-white border border-stone-300 focus:border-bordeaux rounded-lg outline-none px-4 py-3 text-ink placeholder:text-stone-400 transition-colors resize-none"
                                        />
                                        <span className="absolute right-3 bottom-3 text-[11px] text-stone-400">
                                            {message.length}/{200}
                                        </span>
                                    </div>
                                </div>

                                <div className="mt-7" data-testid="ramo-personalize-card">
                                    <div className="border border-stone-200 rounded-xl bg-white p-4 max-w-md">
                                        <p className="text-sm font-bold text-ink">
                                            2. {t("ramo.personalizeTitle")}
                                        </p>
                                        <p className="mt-0.5 text-xs font-semibold text-bordeaux" data-testid="ramo-personalize-summary">
                                            {[redBalloon && t("ramo.redBalloonOption"), balloonTextOn && t("ramo.balloonTextOption")]
                                                .filter(Boolean)
                                                .join(", ") ||
                                                `${t("ramo.redBalloonOption")}, ${t("ramo.balloonTextOption")}`}
                                            {(redBalloon || balloonTextOn) &&
                                                ` : + ${formatPrice(
                                                    (redBalloon ? product.roseExtras.redBalloon.price : 0) +
                                                        (balloonTextOn ? product.roseExtras.balloonText.price : 0),
                                                    lang
                                                )}`}
                                        </p>
                                        <div className="mt-3 space-y-2.5">
                                            <button
                                                onClick={() => setRedBalloon(!redBalloon)}
                                                data-testid="ramo-red-balloon-checkbox"
                                                className="w-full flex items-start justify-between gap-3 border border-stone-200 rounded-lg px-4 py-3.5 text-left hover:border-stone-400 transition-colors bg-white touch-manipulation select-none"
                                            >
                                                <span className="flex items-start gap-3">
                                                    <span
                                                        className={`mt-0.5 h-5 w-5 rounded flex items-center justify-center border shrink-0 transition-colors ${
                                                            redBalloon
                                                                ? "bg-bordeaux border-bordeaux"
                                                                : "bg-white border-stone-300"
                                                        }`}
                                                    >
                                                        {redBalloon && (
                                                            <Check
                                                                className="h-3.5 w-3.5 text-white"
                                                                strokeWidth={3}
                                                            />
                                                        )}
                                                    </span>
                                                    <img
                                                        src="/images/globo-corazon.jpg"
                                                        alt={t("ramo.redBalloonOption")}
                                                        className="h-[60px] w-[60px] rounded-lg object-cover shrink-0 border border-stone-200"
                                                        loading="lazy"
                                                    />
                                                    <span className="text-sm font-bold text-stone-900">
                                                        {t("ramo.redBalloonOption")}
                                                        <span className="block text-xs font-normal text-stone-500 mt-0.5">
                                                            {t("ramo.redBalloonDesc")}
                                                        </span>
                                                    </span>
                                                </span>
                                                <span className="text-sm font-bold text-bordeaux shrink-0">
                                                    + {formatPrice(product.roseExtras.redBalloon.price, lang)}
                                                </span>
                                            </button>
                                            <button
                                                onClick={() => setBalloonTextOn(!balloonTextOn)}
                                                data-testid="ramo-balloon-text-checkbox"
                                                className="w-full flex items-start justify-between gap-3 border border-stone-200 rounded-lg px-4 py-3.5 text-left hover:border-stone-400 transition-colors bg-white touch-manipulation select-none"
                                            >
                                                <span className="flex items-start gap-3">
                                                    <span
                                                        className={`mt-0.5 h-5 w-5 rounded flex items-center justify-center border shrink-0 transition-colors ${
                                                            balloonTextOn
                                                                ? "bg-bordeaux border-bordeaux"
                                                                : "bg-white border-stone-300"
                                                        }`}
                                                    >
                                                        {balloonTextOn && (
                                                            <Check
                                                                className="h-3.5 w-3.5 text-white"
                                                                strokeWidth={3}
                                                            />
                                                        )}
                                                    </span>
                                                    <span className="text-sm font-bold text-stone-900">
                                                        {t("ramo.balloonTextOption")}
                                                    </span>
                                                </span>
                                                <span className="text-sm font-bold text-bordeaux shrink-0">
                                                    + {formatPrice(product.roseExtras.balloonText.price, lang)}
                                                </span>
                                            </button>
                                            {balloonTextOn && (
                                                <div>
                                                    <p className="text-xs font-semibold text-stone-600 mb-1.5">
                                                        {t("ramo.yourTextLabel")}
                                                    </p>
                                                    <div className="relative">
                                                        <input
                                                            type="text"
                                                            value={balloonTextValue}
                                                            maxLength={product.roseExtras.balloonText.maxLength}
                                                            onChange={(e) => setBalloonTextValue(e.target.value)}
                                                            placeholder={t("ramo.charsMax")}
                                                            data-testid="ramo-balloon-text-input"
                                                            className="w-full bg-white border border-stone-300 focus:border-bordeaux rounded-lg outline-none px-4 py-3 pr-14 text-ink placeholder:text-stone-400 transition-colors"
                                                        />
                                                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] text-stone-400">
                                                            {balloonTextValue.length}/{product.roseExtras.balloonText.maxLength}
                                                        </span>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}

                        {product.options.shirt && (
                            <div className="mt-7">
                                <p className="text-sm font-bold text-ink mb-3 flex items-center gap-2">
                                    2. {t("product.opt2")}
                                    <Shirt className="h-4 w-4 text-bordeaux" strokeWidth={2} />
                                </p>
                                <div className="relative max-w-md">
                                    <input
                                        type="text"
                                        value={shirtText}
                                        maxLength={50}
                                        onChange={(e) => setShirtText(e.target.value)}
                                        placeholder={t("product.shirtPlaceholder")}
                                        data-testid="tshirt-text-input"
                                        className="w-full bg-white border border-stone-300 focus:border-bordeaux rounded-lg outline-none px-4 py-3 pr-14 text-ink placeholder:text-stone-400 transition-colors"
                                    />
                                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] text-stone-400">
                                        {shirtText.length}/{50}
                                    </span>
                                </div>
                            </div>
                        )}

                        {isTeddy && (
                            <div className="mt-7">
                                <p className="text-sm font-bold text-ink mb-3">
                                    {t("teddy.colorLabel")}
                                </p>
                                <div className="flex flex-wrap gap-2.5">
                                    {teddy.colors.map((c) => (
                                        <button
                                            key={c.id}
                                            onClick={() => {
                                                setTeddyColor(c.id);
                                                setActiveImage(teddyImageIndex(c.id, hasShirt));
                                            }}
                                            data-testid={`teddy-color-${c.id}`}
                                            className={`flex items-center gap-2.5 px-5 py-3 border rounded-lg text-sm font-semibold transition-colors ${
                                                teddyColor === c.id
                                                    ? "border-stone-900 border-2 text-stone-900"
                                                    : "border-stone-300 text-stone-800 hover:border-stone-500"
                                            }`}
                                        >
                                            <span
                                                className="h-5 w-5 rounded-full border border-stone-300"
                                                style={{ background: c.dot }}
                                            />
                                            {c.label[lang]}
                                        </button>
                                    ))}
                                </div>

                                <div className="mt-6 border border-stone-200 rounded-xl bg-white">
                                    <div className="px-4 py-4 border-b border-stone-100">
                                        <p className="text-base font-bold text-stone-900">
                                            {t("teddy.personTitle")}
                                        </p>
                                        <p className="text-sm text-stone-500 mt-0.5">
                                            {t("teddy.personSub")}
                                        </p>
                                    </div>
                                    <div className="px-4 py-4 space-y-3">
                                        <button
                                            onClick={() => {
                                                setHasShirt(!hasShirt);
                                                setActiveImage(teddyImageIndex(teddyColor, !hasShirt));
                                            }}
                                            data-testid="teddy-shirt-checkbox"
                                            className="w-full flex items-start justify-between gap-3 border border-stone-200 rounded-lg px-4 py-3.5 text-left hover:border-stone-400 transition-colors"
                                        >
                                            <span className="flex items-start gap-3">
                                                <span
                                                    className={`mt-0.5 h-5 w-5 rounded flex items-center justify-center border shrink-0 ${
                                                        hasShirt
                                                            ? "bg-bordeaux border-bordeaux"
                                                            : "bg-white border-stone-300"
                                                    }`}
                                                >
                                                    {hasShirt && (
                                                        <Check className="h-3.5 w-3.5 text-white" strokeWidth={3} />
                                                    )}
                                                </span>
                                                <span>
                                                    <span className="block text-sm font-bold text-stone-900">
                                                        {t("teddy.shirtCheck")}
                                                    </span>
                                                    <span className="block text-xs text-stone-500 mt-0.5">
                                                        {t("teddy.shirtCheckSub")}
                                                    </span>
                                                </span>
                                            </span>
                                            <span className="text-sm font-bold text-stone-900 shrink-0">
                                                + {formatPrice(teddy.shirt.price, lang)}
                                            </span>
                                        </button>

                                        {hasShirt && (
                                            <>
                                                <div className="flex flex-wrap gap-2.5 pl-1">
                                                    {teddy.shirt.colors.map((c) => (
                                                        <button
                                                            key={c.id}
                                                            onClick={() => setShirtColor(c.id)}
                                                            data-testid={`teddy-shirt-color-${c.id}`}
                                                            className={`flex items-center gap-2.5 px-5 py-2.5 border rounded-lg text-sm font-semibold transition-colors ${
                                                                shirtColor === c.id
                                                                    ? "border-stone-900 border-2 text-stone-900"
                                                                    : "border-stone-300 text-stone-800 hover:border-stone-500"
                                                            }`}
                                                        >
                                                            <span
                                                                className="h-5 w-5 rounded-full border border-stone-300"
                                                                style={{ background: c.dot }}
                                                            />
                                                            {c.label[lang]}
                                                        </button>
                                                    ))}
                                                </div>

                                                <button
                                                    onClick={() => setHasShirtText(!hasShirtText)}
                                                    data-testid="teddy-shirt-text-checkbox"
                                                    className="w-full flex items-start justify-between gap-3 border border-stone-200 rounded-lg px-4 py-3.5 text-left hover:border-stone-400 transition-colors"
                                                >
                                                    <span className="flex items-start gap-3">
                                                        <span
                                                            className={`mt-0.5 h-5 w-5 rounded flex items-center justify-center border shrink-0 ${
                                                                hasShirtText
                                                                    ? "bg-bordeaux border-bordeaux"
                                                                    : "bg-white border-stone-300"
                                                            }`}
                                                        >
                                                            {hasShirtText && (
                                                                <Check className="h-3.5 w-3.5 text-white" strokeWidth={3} />
                                                            )}
                                                        </span>
                                                        <span>
                                                            <span className="block text-sm font-bold text-stone-900">
                                                                {t("teddy.shirtTextLabel")}
                                                            </span>
                                                            <span className="block text-xs text-stone-500 mt-0.5">
                                                                {t("teddy.shirtTextSub")}
                                                            </span>
                                                        </span>
                                                    </span>
                                                    <span className="text-sm font-bold text-stone-900 shrink-0">
                                                        + {formatPrice(teddy.shirtText.price, lang)}
                                                    </span>
                                                </button>

                                                {hasShirtText && (
                                                    <div className="pl-1">
                                                        <p className="text-sm font-bold text-ink mb-2">
                                                            {t("teddy.yourText")}
                                                        </p>
                                                        <input
                                                            type="text"
                                                            value={shirtText}
                                                            maxLength={teddy.shirtText.maxLength}
                                                            onChange={(e) => setShirtText(e.target.value)}
                                                            placeholder={t("teddy.charsMax")}
                                                            data-testid="tshirt-text-input"
                                                            className="w-full bg-white border border-stone-300 focus:border-bordeaux rounded-lg outline-none px-4 py-3 text-ink placeholder:text-stone-400 transition-colors"
                                                        />
                                                        <div className="mt-3 flex items-center gap-3">
                                                            <div className="w-16 h-16 rounded-lg bg-stone-900 flex items-center justify-center shrink-0">
                                                                <span className="font-serif italic text-sm text-white text-center px-1 leading-tight break-words">
                                                                    {shirtText || "♡"}
                                                                </span>
                                                            </div>
                                                            <p className="text-[11px] text-stone-500">
                                                                {t("product.shirtPreview")}
                                                            </p>
                                                        </div>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {!isPack && !isCaja && !isRamo && (
                            <div className="mt-7">
                                <p className="text-sm font-bold text-ink mb-3">
                                    {!isTeddy && `${product.options.roseCounts ? 2 : 1}. `}
                                    {t("product.opt3")}
                                </p>
                                <div className="relative max-w-md">
                                    <textarea
                                        value={message}
                                        maxLength={200}
                                        rows={3}
                                        onChange={(e) => setMessage(e.target.value)}
                                        placeholder={t("product.messagePlaceholder")}
                                        data-testid="card-message-input"
                                        className="w-full bg-white border border-stone-300 focus:border-bordeaux rounded-lg outline-none px-4 py-3 text-ink placeholder:text-stone-400 transition-colors resize-none"
                                    />
                                    <span className="absolute right-3 bottom-3 text-[11px] text-stone-400">
                                        {message.length}/{200}
                                    </span>
                                </div>
                            </div>
                        )}

                        {!isPack && product.extras && ExtrasSection()}

                        {isCaja && product.extras && (
                            <div className="mt-4" data-testid="caja-card-message-block">
                                <p className="text-sm font-bold text-ink mb-2">
                                    {t("extras.cardMessageTitle")}{" "}
                                    <span className="text-bordeaux font-bold">
                                        · {t("ramo.freeTag")}
                                    </span>
                                </p>
                                <div className="relative max-w-md">
                                    <textarea
                                        value={message}
                                        maxLength={200}
                                        rows={3}
                                        onChange={(e) => setMessage(e.target.value)}
                                        placeholder={t("product.messagePlaceholder")}
                                        data-testid="card-message-input"
                                        className="w-full bg-white border border-stone-300 focus:border-bordeaux rounded-lg outline-none px-4 py-3 text-ink placeholder:text-stone-400 transition-colors resize-none"
                                    />
                                    <span className="absolute right-3 bottom-3 text-[11px] text-stone-400">
                                        {message.length}/{200}
                                    </span>
                                </div>
                            </div>
                        )}

                        {!isPack && (
                            <>
                                {DeliveryInfoSection({
                                    num: !isTeddy
                                        ? product.roseExtras
                                            ? 3
                                            : product.options.roseCounts
                                              ? 3
                                              : 2
                                        : undefined,
                                })}
                                {WhenSection({
                                    num: !isTeddy
                                        ? product.roseExtras
                                            ? 4
                                            : product.options.roseCounts
                                              ? 4
                                              : 3
                                        : undefined,
                                })}
                            </>
                        )}

                        <div className="mt-8 max-w-md hidden lg:block">
                            <button
                                onClick={handleAdd}
                                data-testid="add-to-cart-button"
                                className="w-full py-4 bg-bordeaux text-white text-sm font-bold rounded-lg hover:bg-bordeaux-deep shadow-md transition-colors"
                            >
                                {t("product.addToCart")} · {formatPrice(price, lang)}
                            </button>
                            <p className="mt-3 text-[11px] italic text-stone-400">
                                {t("product.demoNote")}
                            </p>
                        </div>

                        <div className="mt-8 max-w-md" data-testid="product-reassurance">
                            <div className="grid grid-cols-2 gap-2.5">
                                {[
                                    { emoji: "📍", key: "reassurance1" },
                                    { emoji: "💌", key: "reassurance2" },
                                    { emoji: "🧸", key: "reassurance3" },
                                    { emoji: "📲", key: "reassurance4" },
                                ].map(({ emoji, key }) => (
                                    <div
                                        key={key}
                                        className="flex items-center gap-2.5 bg-white border border-stone-200 rounded-lg px-3 py-2.5"
                                    >
                                        <span
                                            className="text-xl leading-none shrink-0"
                                            aria-hidden="true"
                                        >
                                            {emoji}
                                        </span>
                                        <span className="text-xs font-semibold text-stone-800 leading-tight">
                                            {t(`pack.${key}`)}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="mt-10 max-w-md">
                            <AccordionItem
                                title={t("product.accordionDesc")}
                                open={openAcc === 0}
                                onToggle={() => setOpenAcc(openAcc === 0 ? -1 : 0)}
                                testId="accordion-description"
                            >
                                <p>{product.desc[lang]}</p>
                                {product.includes && (
                                    <div className="mt-3">
                                        <p className="font-semibold text-stone-800">
                                            {t("product.includesTitle")}:
                                        </p>
                                        <ul className="mt-2 space-y-1.5">
                                            {product.includes.map((it, i) => (
                                                <li key={i} className="flex items-start gap-2">
                                                    <Check
                                                        className="h-4 w-4 text-bordeaux mt-0.5 shrink-0"
                                                        strokeWidth={2.5}
                                                    />
                                                    {it[lang]}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </AccordionItem>
                            <AccordionItem
                                title={t("product.accordionDelivery")}
                                open={openAcc === 1}
                                onToggle={() => setOpenAcc(openAcc === 1 ? -1 : 1)}
                                testId="accordion-delivery"
                            >
                                {t("product.deliveryInfo")}
                            </AccordionItem>
                            <AccordionItem
                                title={t("product.accordionFaq")}
                                open={openAcc === 2}
                                onToggle={() => setOpenAcc(openAcc === 2 ? -1 : 2)}
                                testId="accordion-faq"
                            >
                                <div className="space-y-3">
                                    {t("faq.items").map((f, i) => (
                                        <div key={i}>
                                            <p className="font-semibold text-stone-800">{f.q}</p>
                                            <p className="mt-0.5">{f.a}</p>
                                        </div>
                                    ))}
                                </div>
                            </AccordionItem>
                        </div>
                    </div>
                </div>
            </div>

            <section
                className="border-t border-stone-200 bg-white"
                data-testid="product-reviews-section"
            >
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
                    <ReviewsCarousel />
                </div>
            </section>

            <div
                data-testid="mobile-sticky-buy-button"
                className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-stone-200 px-4 py-3 flex items-center justify-between gap-3 shadow-2xl"
            >
                <div className="min-w-0" data-testid="sticky-pack-summary">
                    <p className="text-[11px] text-stone-500 truncate">{product.name[lang]}</p>
                    {(isPack || isRamo) && (
                        <p className="text-[11px] text-stone-500 truncate">
                            {roseCount} {t("product.rosesUnit")}
                            {isPack && shirtName.trim() ? ` · ${shirtName.trim()}` : ""}
                        </p>
                    )}
                    <p className="font-serif italic text-lg text-bordeaux leading-tight">
                        {formatPrice(price, lang)}
                    </p>
                    {hasFreeDelivery && (
                        <p
                            className="text-[11px] font-bold text-bordeaux truncate"
                            data-testid="sticky-free-delivery"
                        >
                            {t("extras.freeShort")}
                        </p>
                    )}
                </div>
                <button
                    onClick={handleAdd}
                    data-testid="add-to-cart-button-mobile"
                    className="flex-1 max-w-[55%] py-3.5 bg-bordeaux text-white text-sm font-bold rounded-lg active:bg-bordeaux-deep transition-colors"
                >
                    {t("product.addToCartShort")} · {formatPrice(price, lang)}
                </button>
            </div>
        </main>
    );
}
